import React from 'react';
import { styled } from '@mui/material';

import { elevations } from '@zendesk/forethought-elements';
import { PanelReadOnlyBanner } from 'src/pages/workflow-builder-edit/customization-panel/PanelReadOnlyBanner';
import { useReadOnlyPanelContext } from 'src/pages/workflow-builder-edit/customization-panel/ReadOnlyPanelContext';

// eslint-disable-next-line react-refresh/only-export-components -- shared styled components
export const CircleIcon = styled('div')<{ bgcolor: string }>`
  background-color: ${props => props.bgcolor};
  border-radius: 16px;
  color: white;
  height: 32px;
  width: 32px;
  padding: 6px;
`;

// eslint-disable-next-line react-refresh/only-export-components -- shared styled components
export const ToolCard = styled('div')<{
  isActive: boolean;
  isDisabled?: boolean;
  isRecommended: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 8px;
  cursor: ${props =>
    props.isDisabled || props.isRecommended ? 'not-allowed' : 'pointer'};
  padding: 10px 16px 10px 16px;
  background-color: ${props => {
    if (props.isDisabled) {
      return props.theme.palette.colors.slate[100];
    }
    return props.theme.palette.colors.white;
  }};
  border: ${props =>
    props.isActive
      ? `1px solid ${props.theme.palette.colors.primary[700]}`
      : `1px solid ${props.theme.palette.colors.grey[200]}`};

  &:not(:hover) {
    .edit-tool-button {
      visibility: hidden;
    }
  }

  &:hover {
    background-color: ${props =>
      !props.isDisabled && props.theme.palette.primary.light};
    border: 1px solid ${props => props.theme.palette.colors.primary[700]};
    box-shadow: ${elevations.z2};
  }
`;

const EditSidePanelContainerBase = styled('div')<{
  noBoxShadow?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: space-between;
  background-color: ${props => props.theme.palette.colors.white};
  gap: 24px;
  padding: 24px;
  overflow: auto;
  width: 650px;
  height: 100%;
  box-shadow: ${props => (props.noBoxShadow ? 'none' : props.theme.shadows[3])};
`;

// All edit tool drawers follow the pattern: first child is the title/header,
// followed by tabs and form content. The read-only banner is injected after
// the header so it appears below the title but above the tabs.
export const EditSidePanelContainer = ({
  children,
  ...props
}: React.ComponentProps<typeof EditSidePanelContainerBase>) => {
  const { isReadOnly, onClose } = useReadOnlyPanelContext();
  const childArray = React.Children.toArray(children);

  return (
    <EditSidePanelContainerBase {...props}>
      {childArray[0]}
      {isReadOnly && onClose && <PanelReadOnlyBanner onClose={onClose} />}
      {childArray.slice(1)}
    </EditSidePanelContainerBase>
  );
};
